import React, { useEffect, useState } from "react";
import { Grid, Box, Typography, TextField, CircularProgress, Link, Stack } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment from "moment";
import { StyledStartTimePicker } from "../../utilities/styles/checkoutPage.style";
import { timezoneDateFormat } from "../../utilities/timezoneDateFormat";

interface PickupDeliveryTimeSelectorProps {
    orderType?: string;
    laterSchedDate: Date;
    requestedPickupAndDeliveryTime: Date;
    minimumPickupDate: Date;
    openHoursList: any;
    calculateEstimatedTimeLoading: boolean;
    timeUpdatedMessage?: string;
    dateUpdatedMessage?: string; 
    scheduleForLater: boolean;
    enableAsap: boolean;
    organizationSettings: any;
    organizationOrdersOpenSchedule: any;
    classes: any;
    onStateChange: (updates: any) => void;
    showStorePolicy?: boolean;
    pickupWindowTime: number;
    deliveryWindowTime: number;
}

const PickupDeliveryTimeSelector: React.FC<PickupDeliveryTimeSelectorProps> = ({
    orderType,
    laterSchedDate,
    requestedPickupAndDeliveryTime,
    minimumPickupDate,
    openHoursList,
    calculateEstimatedTimeLoading,
    scheduleForLater,
    enableAsap,
    organizationSettings,
    organizationOrdersOpenSchedule,
    classes,
    onStateChange,
    showStorePolicy,
    timeUpdatedMessage,
    dateUpdatedMessage,
    pickupWindowTime,
    deliveryWindowTime
}) => {
    const [showHelperText, setShowHelperText] = useState(false);
    const dateFormat = timezoneDateFormat();

    const pickupTime = localStorage.getItem("requestedPickupDateTime");

    const toggleHelperText = () => {
        if(pickupTime && new Date(pickupTime) < new Date()){
            setShowHelperText(true)
        } else {
            setShowHelperText(false);
        }
    }

    useEffect(() => {
        toggleHelperText()
    }, [laterSchedDate, requestedPickupAndDeliveryTime]);

    return (
        <>
            <Grid item xs={12} sm={12} md={10} lg={8}>
                <Box display="flex" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography
                        gutterBottom
                        sx={{
                            mt: 4,
                            fontSize: { sm: "20px" },
                            fontWeight: 600,
                        }}
                    >
                        {orderType === "Pickup" ? "Pickup Time" : "Delivery Time"}
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "15px",
                        }}
                    >
                        {showStorePolicy && (
                            <Box
                                onClick={() => {
                                    onStateChange({ openPolicyModel: true });
                                }}
                                sx={{
                                    mt: 4,
                                    fontSize: { sm: "15px" },
                                    fontWeight: 500,
                                    color: "#0000ef",
                                    cursor: "pointer",
                                }}
                            >
                                {"Store Policy"}
                            </Box>
                        )}
                        <Box
                            onClick={() => {
                                onStateChange({ openOpenHoursModel: true });
                            }}
                            sx={{
                                mt: 4,
                                fontSize: { sm: "15px" },
                                fontWeight: 500,
                                color: "#0000ef",
                                cursor: "pointer",
                            }}
                        >
                            {"Store Hours"}
                        </Box>
                        {organizationSettings?.checkoutLinkName &&
                            organizationSettings?.checkoutLinkURL && (
                                <Link
                                    href={
                                        organizationSettings.checkoutLinkURL.startsWith("http")
                                            ? organizationSettings.checkoutLinkURL
                                            : `https://${organizationSettings.checkoutLinkURL}`
                                    }
                                    target="_blank"
                                    sx={{
                                        mt: 4,
                                        fontSize: { sm: "15px" },
                                        fontWeight: 500,
                                        color: "#0000ef",
                                        cursor: "pointer",
                                    }}
                                    underline="none"
                                >
                                    {organizationSettings.checkoutLinkName}
                                </Link>
                            )}
                    </Box>
                </Box>
            </Grid>
            {!enableAsap || scheduleForLater ? (
                <Grid container>
                    {calculateEstimatedTimeLoading ? (
                        <Grid item xs={12}>
                            <Box sx={{ pl: 5 }}>
                                <CircularProgress color="secondary" size={20} />
                            </Box>
                        </Grid>
                    ) : (
                        <Grid item xs={12} sm={12}>
                            <div className={classes.selectedDateErrorMessage}></div>
                            <div className={classes.scheduleDetails}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <Box sx={{ display: "flex", gap: 3, flex: 1, alignItems: "flex-start"}}>
                                        <Grid marginTop="4px" item xs={12} sm={6}>
                                            <DatePicker
                                                label={
                                                    orderType === "Pickup" ? "Pickup Date" : "Delivery Date"
                                                }
                                                value={laterSchedDate}
                                                onChange={(newValue) =>
                                                    onStateChange({
                                                        laterSchedDate: newValue,
                                                        dateUpdatedMessage: "",
                                                        timeUpdatedMessage: "",
                                                    })
                                                }
                                                disablePast
                                                sx={{
                                                    minWidth: {
                                                        xs: "120px",
                                                        sm: "180px",
                                                    },
                                                    mt: "auto",
                                                }}
                                                format={dateFormat}
                                                slots={{
                                                    textField: TextField,
                                                }}
                                                shouldDisableDate={(date) => {
                                                    const formattedDate = moment(date).format("YYYY-MM-DD");
                                                    const dayOfWeek = moment(date)
                                                        .format("dddd")
                                                        .toLowerCase();
                                                    const daySettings = organizationOrdersOpenSchedule
                                                        ? organizationOrdersOpenSchedule[dayOfWeek]
                                                        : null;
                                                    const isHoliday =
                                                        organizationSettings?.ordersHolidays?.some(
                                                            (holiday: any) =>
                                                                moment(holiday.holidayDate).format(
                                                                    "YYYY-MM-DD"
                                                                ) === formattedDate && holiday.closedAllDay
                                                        );
                                                    const isClosedAllDay =
                                                        daySettings && daySettings.closedAllDay;

                                                    return isHoliday || isClosedAllDay;
                                                }}
                                                minDate={minimumPickupDate}
                                                slotProps={{
                                                    calendarHeader: {
                                                        sx: {
                                                            position: "relative",
                                                            "& .MuiPickersArrowSwitcher-root": {
                                                                width: 0,
                                                            },
                                                            "& .MuiPickersCalendarHeader-labelContainer": {
                                                                mx: "auto",
                                                            },
                                                            "& .MuiIconButton-edgeEnd": {
                                                                position: "absolute",
                                                                left: 0,
                                                                top: 0,
                                                                bottom: 0,
                                                            },
                                                            "& .MuiIconButton-edgeStart": {
                                                                position: "absolute",
                                                                right: 0,
                                                                top: 0,
                                                                bottom: 0,
                                                            },
                                                            "& .MuiPickersCalendarHeader-switchViewIcon": {
                                                                display: "none",
                                                            },
                                                        },
                                                    },
                                                    openPickerButton: {
                                                        sx: {
                                                            "& .MuiSvgIcon-root": {
                                                                height: "26px",
                                                                width: "26px",
                                                                color: "#000",
                                                            },
                                                        },
                                                    },
                                                    textField: {
                                                        variant: "standard",
                                                        required: true,
                                                        id: "date-picker-dialog",
                                                        name: "date-picker-dialog",
                                                        fullWidth: true,
                                                        InputProps: {
                                                            sx: {
                                                                borderRadius: "8px",
                                                                "& .MuiInputBase-input": {
                                                                    fontSize: "18px",
                                                                },
                                                            },
                                                        },
                                                        InputLabelProps: {
                                                            sx: {
                                                                "&.MuiInputLabel-root": {
                                                                    fontSize: "18px",
                                                                },
                                                            },
                                                        },
                                                    },
                                                }}
                                            />
                                            {dateUpdatedMessage  && (
                                                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                                    {dateUpdatedMessage}
                                                </Typography>
                                            )}
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                        <Stack direction="column">
                                            <StyledStartTimePicker
                                                name="pickupTime"
                                                minTime={openHoursList?.openFrom}
                                                maxTime={openHoursList?.openTo}
                                                pickupWindow={orderType === "Pickup" ? pickupWindowTime : deliveryWindowTime}
                                                onChange={(value: any) => {
                                                    onStateChange({
                                                        requestedPickupAndDeliveryTime: value,
                                                        dateUpdatedMessage: "",
                                                        timeUpdatedMessage: "",
                                                    });
                                                    localStorage.setItem("requestedPickupDateTime", value)
                                                }}
                                                label={
                                                    orderType === "Pickup" ? "Pickup Time" : "Delivery Time"
                                                }
                                                dateTime={requestedPickupAndDeliveryTime}
                                                sx={{ minWidth: "100%" }}
                                            />
                                             {showHelperText && (
                                                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                                        Time was updated, please review                                                    
                                                    </Typography>
                                            )}
                                             {timeUpdatedMessage && (
                                                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                                    {timeUpdatedMessage}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Grid>
                                    </Box>
                                </LocalizationProvider>
                            </div>
                        </Grid>
                    )}
                </Grid>
            ) : (
                ""
            )}
        </>
    );
};

export default PickupDeliveryTimeSelector;
